import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { invalidateCatalog } from "../lib/catalog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Lock, Delete, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

export const LOCK_KEY = "nx_shift_locked";

// Bloquea la caja y registra el evento en la bitácora de turnos
export function lockShift(user, { auto = false, cartItems = 0 } = {}) {
  sessionStorage.setItem(LOCK_KEY, "1");
  sessionStorage.setItem(LOCK_KEY + "_by", user?.name || "");
  sessionStorage.setItem(LOCK_KEY + "_cart", String(cartItems));
  api.post("/shift-log", { event: auto ? "auto_lock" : "lock", station_id: localStorage.getItem("pos_station_id") || "", cart_items: cartItems }).catch(() => {});
  window.dispatchEvent(new Event("nx-shift-lock"));
}

// Pantalla de bloqueo: otro cajero entra con su usuario + PIN sin perder la venta en curso
export default function ShiftLock() {
  const { user, logout } = useAuth();
  const [username, setUsername] = useState(() => localStorage.getItem("nx_last_user") || "");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const lockedBy = sessionStorage.getItem(LOCK_KEY + "_by") || user?.name || "";

  const unlock = async (e) => {
    e?.preventDefault();
    if (!username.trim() || !pin) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: username.trim(), password: pin, business_code: user.business_id }, { __skipOffline: true });
      if (data.user?.business_id !== user.business_id) throw new Error("El usuario no pertenece a esta empresa");
      localStorage.setItem("nx_token", data.access_token);
      const st = localStorage.getItem("pos_station_id") || "";
      await api.post("/shift-log", { event: "unlock", station_id: st, target_user: username.trim(), cart_items: Number(sessionStorage.getItem(LOCK_KEY + "_cart") || 0) }).catch(() => {});
      localStorage.setItem("nx_last_user", username.trim());
      sessionStorage.removeItem(LOCK_KEY); sessionStorage.removeItem(LOCK_KEY + "_by");
      sessionStorage.setItem("nx_branch_chosen", "1");
      if (data.user?.id !== user.id) invalidateCatalog();
      toast.success(`Turno de ${data.user?.name || username}`);
      window.location.reload();
    } catch (err) {
      api.post("/shift-log", { event: "unlock_failed", station_id: localStorage.getItem("pos_station_id") || "", target_user: username.trim() }).catch(() => {});
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "PIN incorrecto");
      setPin(""); setBusy(false);
    }
  };
  const doLogout = async () => { sessionStorage.removeItem(LOCK_KEY); sessionStorage.removeItem(LOCK_KEY + "_by"); await logout(); window.location.href = "/login"; };

  const key = (d) => setPin((p) => p + d);
  return (
    <div className="fixed inset-0 z-[200] grid place-items-center p-4" style={{ background: "#020d1f" }} data-testid="shift-lock">
      <form onSubmit={unlock} className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl space-y-4 text-slate-900">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-100 grid place-items-center"><Lock className="text-amber-600" size={22} /></div>
          <div>
            <h2 className="font-display font-bold text-lg leading-tight">Caja bloqueada</h2>
            <p className="text-xs text-muted-foreground">{lockedBy ? `Bloqueada por ${lockedBy} · ` : ""}la venta en curso se conserva.</p>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Usuario</label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ej. cajero1" className="mt-1 h-11" autoFocus={!username} data-testid="lock-user" />
        </div>
        <div>
          <label className="text-sm font-medium">PIN / Contraseña</label>
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" className="mt-1 h-11 text-center text-xl tracking-widest" autoFocus={!!username} data-testid="lock-pin" />
        </div>
        <div className="grid grid-cols-3 gap-2 select-none" data-testid="lock-pinpad">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button key={d} type="button" onClick={() => key(d)} className="h-12 rounded-xl border border-border bg-white text-xl font-bold hover:bg-emerald-50 hover:border-emerald-400 active:scale-95 transition-transform" data-testid={`lock-pin-${d}`}>{d}</button>
          ))}
          <button type="button" onClick={() => setPin("")} className="h-12 rounded-xl border border-border bg-slate-50 text-xs font-bold text-slate-600 hover:bg-red-50" data-testid="lock-pin-clear">Borrar todo</button>
          <button type="button" onClick={() => key("0")} className="h-12 rounded-xl border border-border bg-white text-xl font-bold hover:bg-emerald-50 hover:border-emerald-400 active:scale-95 transition-transform" data-testid="lock-pin-0">0</button>
          <button type="button" onClick={() => setPin((p) => p.slice(0, -1))} className="h-12 rounded-xl border border-border bg-slate-50 grid place-items-center text-slate-600 hover:bg-amber-50" data-testid="lock-pin-backspace" aria-label="Borrar"><Delete size={20} /></button>
        </div>
        <Button type="submit" disabled={busy || !username.trim() || !pin} className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-bold" data-testid="lock-unlock">
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Entrar al turno"}
        </Button>
        <button type="button" onClick={doLogout} className="w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-red-600" data-testid="lock-logout"><LogOut size={12} /> Cerrar sesión por completo</button>
      </form>
    </div>
  );
}
